/**
 * Transform math for scene documents, matching prism_math: `TransformDesc`
 * rotation is Euler XYZ in degrees (`Rx * Ry * Rz`, glam's `EulerRot::XYZ`),
 * matrices are column-major 4x4 (`m[col * 4 + row]`), `M = T * R * S`.
 */

import type { SceneDoc, TransformDesc, Vec3 } from './scene-doc.js';

export type Mat4 = number[];

const DEG = Math.PI / 180;

export const IDENTITY_MAT: Mat4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

/** 3x3 rotation (row-major `r[row][col]`) of Euler XYZ degrees. */
function rotationRows([ax, ay, az]: Vec3): number[][] {
  const [ca, sa] = [Math.cos(ax * DEG), Math.sin(ax * DEG)];
  const [cb, sb] = [Math.cos(ay * DEG), Math.sin(ay * DEG)];
  const [cc, sc] = [Math.cos(az * DEG), Math.sin(az * DEG)];
  return [
    [cb * cc, -cb * sc, sb],
    [ca * sc + sa * sb * cc, ca * cc - sa * sb * sc, -sa * cb],
    [sa * sc - ca * sb * cc, sa * cc + ca * sb * sc, ca * cb],
  ];
}

export function composeMatrix(t: TransformDesc | undefined): Mat4 {
  const position = t?.position ?? [0, 0, 0];
  const rotation = t?.rotation ?? [0, 0, 0];
  const scale = t?.scale ?? [1, 1, 1];
  const r = rotationRows(rotation);
  const m = new Array<number>(16).fill(0);
  for (let col = 0; col < 3; col += 1) {
    for (let row = 0; row < 3; row += 1) m[col * 4 + row] = r[row]![col]! * scale[col]!;
  }
  m[12] = position[0];
  m[13] = position[1];
  m[14] = position[2];
  m[15] = 1;
  return m;
}

export function multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Array<number>(16).fill(0);
  for (let col = 0; col < 4; col += 1) {
    for (let row = 0; row < 4; row += 1) {
      let s = 0;
      for (let k = 0; k < 4; k += 1) s += a[k * 4 + row]! * b[col * 4 + k]!;
      out[col * 4 + row] = s;
    }
  }
  return out;
}

/** Inverse of an affine matrix (last row 0 0 0 1). */
export function invertAffine(m: Mat4): Mat4 {
  const [a, b, c, d, e, f, g, h, i] = [m[0]!, m[4]!, m[8]!, m[1]!, m[5]!, m[9]!, m[2]!, m[6]!, m[10]!];
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(det) < 1e-12) return IDENTITY_MAT.slice();
  const inv = [
    (e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det,
    (f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det,
    (d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det,
  ];
  const out = new Array<number>(16).fill(0);
  // inv is row-major 3x3.
  for (let row = 0; row < 3; row += 1) for (let col = 0; col < 3; col += 1) out[col * 4 + row] = inv[row * 3 + col]!;
  const [tx, ty, tz] = [m[12]!, m[13]!, m[14]!];
  for (let row = 0; row < 3; row += 1) {
    out[12 + row] = -(inv[row * 3]! * tx + inv[row * 3 + 1]! * ty + inv[row * 3 + 2]! * tz);
  }
  out[15] = 1;
  return out;
}

const round = (v: number) => {
  const r = Math.round(v * 1e5) / 1e5;
  return Object.is(r, -0) ? 0 : r;
};

/** Position / Euler XYZ degrees / scale of an affine matrix (no shear). */
export function decomposeMatrix(m: Mat4): Required<TransformDesc> {
  const col = (c: number): Vec3 => [m[c * 4]!, m[c * 4 + 1]!, m[c * 4 + 2]!];
  const len = (v: Vec3) => Math.hypot(v[0], v[1], v[2]);
  const [c0, c1, c2] = [col(0), col(1), col(2)];
  let sx = len(c0);
  const [sy, sz] = [len(c1), len(c2)];
  const det = c0[0] * (c1[1] * c2[2] - c1[2] * c2[1]) - c1[0] * (c0[1] * c2[2] - c0[2] * c2[1]) + c2[0] * (c0[1] * c1[2] - c0[2] * c1[1]);
  if (det < 0) sx = -sx;
  // r[row][col]
  const r = (row: number, c: number) => m[c * 4 + row]! / ([sx, sy, sz][c] || 1);
  const ay = Math.asin(Math.max(-1, Math.min(1, r(0, 2))));
  let ax: number;
  let az: number;
  if (Math.abs(r(0, 2)) < 0.999999) {
    ax = Math.atan2(-r(1, 2), r(2, 2));
    az = Math.atan2(-r(0, 1), r(0, 0));
  } else {
    // Gimbal lock: put the remaining turn on X.
    ax = Math.atan2(r(2, 1), r(1, 1));
    az = 0;
  }
  return {
    position: [round(m[12]!), round(m[13]!), round(m[14]!)],
    rotation: [round(ax / DEG), round(ay / DEG), round(az / DEG)],
    scale: [round(sx), round(sy), round(sz)],
  };
}

/** World matrix of entity `id` from the document's hierarchy (prism_core `world_matrix`). */
export function worldMatrix(doc: SceneDoc, id: string): Mat4 {
  let m = IDENTITY_MAT.slice();
  let current = doc.entities.find((e) => e.id === id);
  for (let depth = 0; current && depth <= doc.entities.length; depth += 1) {
    m = multiply(composeMatrix(current.transform), m);
    const parent = current.parent;
    current = parent ? doc.entities.find((e) => e.id === parent) : undefined;
  }
  return m;
}
